"use client"

/**
 * The guided onboarding step bodies.
 *
 * These are NOT the finance management pages. Each step is purpose-built for the guided flow — plain-language
 * framing, a calm list of what's been set up, and a single obvious action — while reusing the real data hooks
 * and create dialogs underneath so the accounting stays identical.
 */

import * as React from "react"
import {
  BanknoteIcon,
  ChevronDownIcon,
  HandCoinsIcon,
  LandmarkIcon,
  type LucideIcon,
  PencilIcon,
  PiggyBankIcon,
  PlusIcon,
  ReceiptTextIcon,
  SmartphoneIcon,
  Trash2Icon,
} from "lucide-react"
import { toast } from "sonner"

import { Currency, isZeroMoney } from "@ibada/shared-types/finance"
import { AccountingTimezone, isSupportedTimezone } from "@ibada/shared-types/period"
import {
  Button,
  ConfirmActionDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  HelperMessage,
  Input,
  Label,
  Select,
  type SelectOption,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  cn,
} from "@ibada/ui"

import { useLocale, useTranslations } from "@/i18n"
import {
  MobileFormInputRow,
  MobileFormSection,
  MobileFormSelectRow,
  MobileFormSheet,
  MobileFormTextArea,
} from "@/shared/components/mobile-form"
import { RowActionsMenu } from "@/shared/components/row-actions-menu"
import { MobileSegmentedControl } from "@/shared/components/mobile-list-chrome"
import { useIsMobile } from "@/shared/hooks/use-mobile"
import { useActiveBranchTimezone } from "@/features/auth/hooks/use-active-branch-timezone"
import { useApiErrorToast } from "@/shared/hooks/use-api-error"
import {
  CURRENT_YEAR,
  YEAR_OPTIONS,
  monthOptions,
  conversionDayInTimezone,
  defaultConversionDay,
  openingBalanceAsAt,
  toConversionDay,
} from "@/features/finance/lib/conversion-date"
import { formatMoney, getCurrencyPrefix } from "@/features/finance/lib/finance-utils"
import { FinanceAccountFormDialog } from "@/features/finance/components/accounts/finance-account-form-dialog"
import { FinanceChartAccountFormDialog } from "@/features/finance/components/accounts/finance-chart-account-form-dialog"
import {
  AccountingAccountType,
  ChartAccountKind,
  type ChartAccount,
} from "@/features/finance/model/accounting-reports-types"
import {
  FinanceAccountType,
  FinanceFundListKind,
  FinanceFundStatus,
  type FinanceAccountRecord,
  type FinanceFundRecord,
} from "@/features/finance/model/finance-types"
import {
  useAccountingChartQuery,
  useFinanceAccountsQuery,
  useFinanceCreateSetupFundMutation,
  useFinanceDeactivateAccountMutation,
  useFinanceFundsQuery,
  useFinanceSetFundStatusMutation,
} from "@/features/finance/services/finance-query"
import {
  isParishFirstSetupBlocked,
  useCompleteFinanceSetupMutation,
  useFinanceSetupQuery,
} from "@/features/finance/services/finance-setup-api"
import { FundFormDialog } from "@/features/finance/components/funds/fund-form-dialog"
import { useBranchCurrency } from "@/features/finance/hooks/use-branch-currency"
import { resolveFinanceCategoryScope } from "@/features/finance/model/finance-branch-resolution"

type StepProps = {
  context: "branch" | "parish"
  workspaceId: string
  branchId: string
  canEdit: boolean
  canCreateChartAccounts?: boolean
  /** Set when the branch owns the accounts it creates (BRANCH_AUTONOMY); null for a workspace-wide chart. */
  chartOwningBranchId?: string | null
  /** Parish-first setup exists only in multi-branch workspaces. */
  parishFirstSetupApplies?: boolean
}

const scopeOf = resolveFinanceCategoryScope

/* -------------------------------------------------------------------------------------------------- */
/* Shared presentation                                                                                 */
/* -------------------------------------------------------------------------------------------------- */

type GuidedItem = {
  id: string
  Icon: LucideIcon
  title: string
  subtitle?: string | null
  meta?: string | null
  /**
   * Change this row. Nothing in the wizard is committed until Complete, so a row put in five minutes ago has
   * to stay open to a second look — the case that prompted this was an account created without its opening
   * balance, which then made the allocation step impossible to balance and offered no way back to the cause.
   *
   * Absent where the domain does not allow it: a SYSTEM_LINKED fund mirrors its income account and is not the
   * church's to rename.
   */
  onEdit?: () => void
  /** Take this row out. Absent where removal is not the church's to make. Always confirmed first. */
  onRemove?: () => void
  /** What the confirmation asks, when `onRemove` is set. */
  removeConfirm?: { title: string; description: string; confirmLabel: string }
}

/** A calm list of what's been set up, with one clear "add" affordance. The only framed element on a step. */
function GuidedList({
  items,
  isLoading,
  addLabel,
  onAdd,
  canEdit,
  emptyIcon: EmptyIcon,
  emptyText,
  collapsedSummary,
  collapsedCountLabel,
  defaultCollapsed = false,
  collapsible = true,
  editLabel,
  removeLabel,
}: {
  items: GuidedItem[]
  isLoading: boolean
  addLabel: string
  onAdd: () => void
  canEdit: boolean
  emptyIcon: LucideIcon
  emptyText: string
  collapsedSummary?: string
  collapsedCountLabel?: string
  defaultCollapsed?: boolean
  collapsible?: boolean
  /** Accessible names for the per-row controls. Required once any item carries an action. */
  editLabel?: string
  removeLabel?: string
}) {
  const [collapsed, setCollapsed] = React.useState(defaultCollapsed)
  const hasSummary = Boolean(collapsedSummary && items.length > 0 && !isLoading)
  const canCollapse = collapsible && hasSummary
  const isMobile = useIsMobile()

  React.useEffect(() => {
    if (items.length === 0) setCollapsed(false)
  }, [items.length])

  // Phone: one grouped list. "Add" is its first row, a long list folds behind a summary row, and each row's
  // change / remove sit in the shared actions sheet.
  if (isMobile) {
    const addRow = canEdit ? (
      <button type='button' onClick={onAdd} className='flex min-h-[3.25rem] w-full items-center gap-3 px-4 text-left active:bg-muted'>
        <span className='flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground'>
          <PlusIcon className='size-[1.125rem]' aria-hidden='true' />
        </span>
        <span className='min-w-0 flex-1 truncate text-base font-extrabold text-primary'>{addLabel}</span>
      </button>
    ) : null
    if (isLoading) return <div className='h-36 animate-pulse rounded-2xl bg-card' />
    return (
      <div className='divide-y divide-border/60 overflow-hidden rounded-2xl bg-card'>
        {addRow}
        {items.length === 0 ? (
          <div className='flex items-center gap-3 px-4 py-3.5'>
            <span className='flex size-9 shrink-0 items-center justify-center rounded-xl bg-muted text-muted-foreground'>
              <EmptyIcon className='size-[1.125rem]' aria-hidden='true' />
            </span>
            <p className='min-w-0 flex-1 text-sm font-semibold text-muted-foreground'>{emptyText}</p>
          </div>
        ) : null}
        {canCollapse ? (
          <button
            type='button'
            onClick={() => setCollapsed((value) => !value)}
            aria-expanded={!collapsed}
            className='flex min-h-12 w-full items-center gap-2 px-4 text-left active:bg-muted'
          >
            <span className='min-w-0 flex-1 truncate text-sm font-bold text-muted-foreground'>{collapsedSummary}</span>
            {collapsedCountLabel ? <span className='shrink-0 text-sm font-bold text-muted-foreground tabular-nums'>{collapsedCountLabel}</span> : null}
            <ChevronDownIcon className={cn("size-4 shrink-0 text-muted-foreground/60 transition-transform", !collapsed && "rotate-180")} aria-hidden='true' />
          </button>
        ) : null}
        {!collapsed || !canCollapse
          ? items.map((item) => <GuidedMobileRow key={item.id} item={item} canEdit={canEdit} editLabel={editLabel} removeLabel={removeLabel} />)
          : null}
      </div>
    )
  }

  return (
    <div className='space-y-3'>
      {isLoading ? (
        <div className='divide-y rounded-xl border'>
          {[0, 1].map((i) => (
            <div key={i} className='flex items-center gap-3 p-3'>
              <span className='bg-muted size-9 shrink-0 animate-pulse rounded-lg' />
              <span className='bg-muted h-4 w-40 animate-pulse rounded' />
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className='flex flex-wrap items-center gap-3 rounded-xl border border-dashed p-3'>
          <span className='bg-muted text-muted-foreground flex size-9 shrink-0 items-center justify-center rounded-lg'>
            <EmptyIcon className='size-4.5' aria-hidden='true' />
          </span>
          <p className='text-muted-foreground min-w-0 flex-1 text-sm'>{emptyText}</p>
          {canEdit ? (
            <Button variant='outline' size='sm' onClick={onAdd}>
              <PlusIcon />
              {addLabel}
            </Button>
          ) : null}
        </div>
      ) : (
        <>
          {/*
            Above the list, not under it. A chart of accounts grows, and the add button was pinned to the
            bottom of however long it had become — so adding the fifteenth account meant scrolling past the
            fourteen you already had, every time. The empty state keeps its inline button: there is nothing
            to scroll past there, and the offer belongs next to the explanation of why the list is empty.
          */}
          {canEdit ? (
            <div className='flex justify-end'>
              <Button variant='outline' size='sm' onClick={onAdd}>
                <PlusIcon />
                {addLabel}
              </Button>
            </div>
          ) : null}
          <div className='divide-y overflow-hidden rounded-xl border'>
            {/*
              Chrome, not content. This used to be built exactly like a row — same icon tile, same title and
              subtitle, same height — so "Fonds existants / 17 configuré(s)" read as a fund called "Fonds
              existants". A control that looks like the things it controls is a control nobody finds. It is
              a bar now: tinted, shorter, no icon tile, label in the muted small caps the rest of the app
              uses for section headers, and the chevron leading rather than trailing.
            */}
            {canCollapse ? (
              <button
                type='button'
                // Same height as a row — the icon tile sets it, so match its box without drawing the tile.
                className='bg-muted/40 hover:bg-muted/70 flex min-h-[3.75rem] w-full items-center gap-2 px-3 text-left transition-colors'
                onClick={() => setCollapsed((value) => !value)}
                aria-expanded={!collapsed}
              >
                <ChevronDownIcon
                  className={cn(
                    "text-muted-foreground size-4 shrink-0 transition-transform",
                    !collapsed && "rotate-180",
                  )}
                  aria-hidden='true'
                />
                <span className='text-muted-foreground min-w-0 flex-1 truncate text-xs font-medium tracking-wide uppercase'>
                  {collapsedSummary}
                </span>
                {collapsedCountLabel ? (
                  <span className='text-muted-foreground shrink-0 text-xs tabular-nums'>
                    {collapsedCountLabel}
                  </span>
                ) : null}
              </button>
            ) : hasSummary ? (
              <div className='bg-muted/40 flex min-h-[3.75rem] w-full items-center gap-2 px-3 text-left'>
                <span className='text-muted-foreground size-4 shrink-0' aria-hidden='true' />
                <span className='text-muted-foreground min-w-0 flex-1 truncate text-xs font-medium tracking-wide uppercase'>
                  {collapsedSummary}
                </span>
                {collapsedCountLabel ? (
                  <span className='text-muted-foreground shrink-0 text-xs tabular-nums'>
                    {collapsedCountLabel}
                  </span>
                ) : null}
              </div>
            ) : null}

            {!collapsed || !canCollapse
              ? items.map((item) => (
                  <div key={item.id} className='flex items-center gap-3 p-3'>
                    <span className='bg-primary/10 text-primary flex size-9 shrink-0 items-center justify-center rounded-lg'>
                      <item.Icon className='size-4.5' aria-hidden='true' />
                    </span>
                    <div className='min-w-0 flex-1'>
                      <p className='truncate font-medium'>{item.title}</p>
                      {item.subtitle ? (
                        <p className='text-muted-foreground truncate text-sm'>{item.subtitle}</p>
                      ) : null}
                    </div>
                    {item.meta ? <p className='shrink-0 text-sm font-medium tabular-nums'>{item.meta}</p> : null}
                    {canEdit && (item.onEdit || item.onRemove) ? (
                      <RowActions item={item} editLabel={editLabel} removeLabel={removeLabel} />
                    ) : null}
                  </div>
                ))
              : null}
          </div>
        </>
      )}
    </div>
  )
}

/** One row of a setup list on a phone: icon, name, detail, figure, and ⋯ opening change / remove in a sheet. */
function GuidedMobileRow({
  item,
  canEdit,
  editLabel,
  removeLabel,
}: {
  item: GuidedItem
  canEdit: boolean
  editLabel?: string
  removeLabel?: string
}) {
  const [confirming, setConfirming] = React.useState(false)
  const header = (
    <span className='flex min-w-0 items-center gap-3'>
      <span className='flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary'>
        <item.Icon className='size-[1.125rem]' aria-hidden='true' />
      </span>
      <span className='truncate text-[15px] font-extrabold text-foreground'>{item.title}</span>
    </span>
  )
  return (
    <div className='flex min-h-14 items-center gap-3 py-2 pr-2 pl-4'>
      <span className='flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary'>
        <item.Icon className='size-[1.125rem]' aria-hidden='true' />
      </span>
      <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
        <span className='truncate text-[15px] leading-tight font-bold text-foreground'>{item.title}</span>
        {item.subtitle ? <span className='truncate text-xs font-semibold text-muted-foreground'>{item.subtitle}</span> : null}
      </span>
      {item.meta ? <span className='max-w-[40%] shrink-0 truncate text-[15px] font-extrabold tabular-nums'>{item.meta}</span> : null}
      {canEdit && (item.onEdit || item.onRemove) ? (
        <>
          <RowActionsMenu
            label={item.title}
            openLabel={editLabel ?? item.title}
            sheetHeader={header}
            groups={[
              item.onEdit ? [{ id: "edit", label: editLabel ?? "", icon: PencilIcon, onSelect: item.onEdit }] : [],
              item.onRemove
                ? [{ id: "remove", label: removeLabel ?? "", icon: Trash2Icon, destructive: true, onSelect: () => setConfirming(true) }]
                : [],
            ]}
          />
          {item.onRemove ? (
            <ConfirmActionDialog
              open={confirming}
              onOpenChange={setConfirming}
              title={item.removeConfirm?.title ?? ""}
              description={item.removeConfirm?.description ?? ""}
              confirmLabel={item.removeConfirm?.confirmLabel ?? ""}
              confirmVariant='destructive'
              onConfirm={() => {
                item.onRemove?.()
                setConfirming(false)
              }}
            />
          ) : null}
        </>
      ) : null}
    </div>
  )
}

/**
 * The per-row controls: change it, or take it out.
 *
 * Inline rather than behind an overflow menu. These rows are the wizard's answer to "what have I set up so
 * far", and the whole complaint was that what it listed could not be corrected — an affordance you have to go
 * looking for would not have answered it. Removal is red and always asks first.
 */
function RowActions({
  item,
  editLabel,
  removeLabel,
}: {
  item: GuidedItem
  editLabel?: string
  removeLabel?: string
}) {
  const [confirming, setConfirming] = React.useState(false)

  return (
    <div className='flex shrink-0 items-center gap-0.5'>
      {item.onEdit ? (
        <Button
          type='button'
          variant='ghost'
          size='icon-sm'
          onClick={item.onEdit}
          aria-label={editLabel}
          title={editLabel}
        >
          <PencilIcon className='size-4' />
        </Button>
      ) : null}

      {item.onRemove ? (
        <>
          <Button
            type='button'
            variant='ghost'
            size='icon-sm'
            className='text-destructive hover:text-destructive hover:bg-destructive/10'
            onClick={() => setConfirming(true)}
            aria-label={removeLabel}
            title={removeLabel}
          >
            <Trash2Icon className='size-4' />
          </Button>
          <ConfirmActionDialog
            open={confirming}
            onOpenChange={setConfirming}
            title={item.removeConfirm?.title ?? ""}
            description={item.removeConfirm?.description ?? ""}
            confirmLabel={item.removeConfirm?.confirmLabel ?? ""}
            confirmVariant='destructive'
            onConfirm={() => {
              item.onRemove?.()
              setConfirming(false)
            }}
          />
        </>
      ) : null}
    </div>
  )
}

/** A guided field: a clear label, the control, and one plain-language line about why it matters. */
function Field({ label, hint, children }: { label: string; hint: string; children: React.ReactNode }) {
  return (
    <div className='space-y-1.5'>
      <Label>{label}</Label>
      {children}
      <p className='text-muted-foreground text-xs leading-relaxed'>{hint}</p>
    </div>
  )
}

/* -------------------------------------------------------------------------------------------------- */
/* Step 1 — Accounting information                                                                      */
/* -------------------------------------------------------------------------------------------------- */

const CURRENCY_OPTIONS: SelectOption<Currency>[] = Object.values(Currency).map((currency) => ({
  value: currency,
  label: `${currency} (${getCurrencyPrefix(currency)})`,
}))

const TIMEZONE_OPTIONS: SelectOption<AccountingTimezone>[] = Object.values(AccountingTimezone).map((zone) => ({
  value: zone,
  label: zone.replace(/_/g, " "),
}))

export type AccountingStepHandle = { save: () => Promise<boolean> }

export const OnboardingAccountingStep = React.forwardRef<
  AccountingStepHandle,
  StepProps & { onReadyChange?: (ready: boolean) => void }
>(function OnboardingAccountingStep({ workspaceId, branchId, parishFirstSetupApplies = true, onReadyChange }, ref) {
  const t = useTranslations("finance.onboarding.accounting")
  const tMobile = useTranslations("finance.onboarding.mobile")
  const toastError = useApiErrorToast("finance")
  const branchTimezone = useActiveBranchTimezone(branchId)
  const locale = useLocale()
  const isMobile = useIsMobile()
  const MONTH_OPTIONS = React.useMemo(() => monthOptions(locale), [locale])

  const setupQuery = useFinanceSetupQuery(workspaceId, branchId)
  const completeMutation = useCompleteFinanceSetupMutation(workspaceId, branchId)
  const status = setupQuery.data ?? null

  const [currency, setCurrency] = React.useState<Currency | null>(null)
  const [startMonth, setStartMonth] = React.useState<string | null>(null)
  const [conversionDate, setConversionDate] = React.useState<string | null>(null)
  const [timezone, setTimezone] = React.useState<AccountingTimezone | null>(null)

  React.useEffect(() => {
    if (!status) return
    setCurrency((c) => c ?? status.baseCurrency)
    setStartMonth((c) => c ?? String(status.fiscalYearStartMonth))
    setTimezone((c) => c ?? (isSupportedTimezone(branchTimezone) ? branchTimezone : AccountingTimezone.AfricaKigali))
    setConversionDate((c) => c ?? (conversionDayInTimezone(status.conversionDate, branchTimezone) || defaultConversionDay()))
  }, [branchTimezone, status])

  const locked = Boolean(status?.isLocked)
  // Cascade (B3): a branch's fiscal year is the parish's, org-wide and locked — shown read-only, never chosen.
  const fiscalYearInherited = Boolean(status?.fiscalYearInherited)
  // Gate (B1): a branch cannot configure finance until the parish's is done. The backend enforces it (409); the
  // wizard blocks up front so the user is not dropped into a form whose submit is guaranteed to fail.
  const parishBlocked = parishFirstSetupApplies && isParishFirstSetupBlocked(status)
  /*
   * Currency is the WORKSPACE's (ADR-0023) — one per workspace, mirrored onto every branch. A
   * self-configuring context (the parish, or a mono-branch workspace's single branch) chooses it HERE,
   * because this is the screen where a church states what its numbers mean; the server writes it to the
   * workspace and cascades. A cascading branch inherits and shows it read-only, and any branch locks once
   * its ledger has posted — a currency describes existing entries, it cannot restate them.
   */
  const currencyEditable = !locked && !parishBlocked && !fiscalYearInherited
  const openingAsAt = openingBalanceAsAt(conversionDate, locale)
  const conversionYear = conversionDate ? conversionDate.slice(0, 4) : null
  const conversionMonth = conversionDate ? String(Number(conversionDate.slice(5, 7))) : null

  // Whether the step can advance. `save()` is driven by the wizard's Continue button, so this step owns no
  // save button of its own — one action, at the bottom of the wizard.
  const ready = !parishBlocked && (locked || Boolean(currency && startMonth && conversionDate && timezone))

  React.useEffect(() => {
    onReadyChange?.(ready)
  }, [ready, onReadyChange])

  const save = React.useCallback(async (): Promise<boolean> => {
    if (parishBlocked) return false
    if (locked) return true
    if (!currency || !startMonth || !conversionDate || !timezone) return false
    try {
      await completeMutation.mutateAsync({
        // Sent only where it is the caller's to choose; a cascading branch inherits the workspace's.
        ...(currencyEditable ? { baseCurrency: currency } : {}),
        timezone,
        fiscalYearStartMonth: Number(startMonth),
        conversionDate,
      })
      return true
    } catch (error) {
      toastError(error)
      return false
    }
  }, [parishBlocked, locked, currencyEditable, currency, startMonth, conversionDate, timezone, completeMutation, toastError])

  React.useImperativeHandle(ref, () => ({ save }), [save])

  if (setupQuery.isLoading) return <p className='text-muted-foreground text-sm'>{t("loading")}</p>

  // Phone: the same choices as grouped rows that open the app's picker sheet, each hint under its group.
  if (isMobile) {
    const picker = { doneLabel: tMobile("done"), emptyLabel: tMobile("noResults") }
    const toOptions = (options: SelectOption<string>[]) => options.map((option) => ({ value: option.value, label: String(option.label) }))
    return (
      <div className='flex flex-col gap-5'>
        {locked ? (
          <HelperMessage tone='info' size='md'>
            {t("locked", { count: status?.postedEntries ?? 0 })}
          </HelperMessage>
        ) : null}
        {parishBlocked ? (
          <HelperMessage tone='warning' size='md'>
            {t("parishFirst")}
          </HelperMessage>
        ) : null}

        <MobileFormSection footer={currencyEditable ? t("currencyHint") : t("currencyWorkspaceHint")}>
          <MobileFormSelectRow
            {...picker}
            label={t("currencyLabel")}
            placeholder={tMobile("choose")}
            value={currency}
            onChange={(value) => value && setCurrency(value as Currency)}
            options={toOptions(CURRENCY_OPTIONS)}
            searchPlaceholder={t("currencyLabel")}
            disabled={!currencyEditable}
          />
        </MobileFormSection>

        <MobileFormSection footer={fiscalYearInherited ? t("yearStartInheritedHint") : t("yearStartHint")}>
          <MobileFormSelectRow
            {...picker}
            label={t("yearStartLabel")}
            placeholder={tMobile("choose")}
            value={startMonth}
            onChange={(value) => value && setStartMonth(value)}
            options={toOptions(MONTH_OPTIONS)}
            disabled={locked || fiscalYearInherited || parishBlocked}
          />
        </MobileFormSection>

        {status ? (
          <MobileFormSection footer={t("timezoneHint")}>
            <MobileFormSelectRow
              {...picker}
              label={t("timezoneLabel")}
              placeholder={tMobile("choose")}
              value={timezone}
              onChange={(value) => value && setTimezone(value as AccountingTimezone)}
              options={toOptions(TIMEZONE_OPTIONS)}
              searchPlaceholder={t("timezoneLabel")}
              disabled={locked || parishBlocked}
            />
          </MobileFormSection>
        ) : null}

        <MobileFormSection
          title={t("startDateLabel")}
          footer={
            <span className='flex flex-col gap-1'>
              <span>{t("startDateHint")}</span>
              {openingAsAt ? <span className='font-bold text-primary'>{t("openingAsAt", { date: openingAsAt })}</span> : null}
            </span>
          }
        >
          <MobileFormSelectRow
            {...picker}
            label={tMobile("month")}
            placeholder={tMobile("choose")}
            value={conversionMonth}
            onChange={(value) => value && setConversionDate(toConversionDay(conversionYear ?? String(CURRENT_YEAR), value))}
            options={toOptions(MONTH_OPTIONS)}
            disabled={locked || parishBlocked}
          />
          <MobileFormSelectRow
            {...picker}
            label={tMobile("year")}
            placeholder={tMobile("choose")}
            value={conversionYear}
            onChange={(value) => value && setConversionDate(toConversionDay(value, conversionMonth ?? "1"))}
            options={toOptions(YEAR_OPTIONS)}
            disabled={locked || parishBlocked}
          />
        </MobileFormSection>
      </div>
    )
  }

  return (
    <div className='space-y-6'>
      {locked ? (
        <HelperMessage tone='info' size='md'>
          {t("locked", { count: status?.postedEntries ?? 0 })}
        </HelperMessage>
      ) : null}

      {parishBlocked ? (
        <HelperMessage tone='warning' size='md'>
          {t("parishFirst")}
        </HelperMessage>
      ) : null}

      <div className='grid gap-5 sm:grid-cols-2'>
        <Field label={t("currencyLabel")} hint={currencyEditable ? t("currencyHint") : t("currencyWorkspaceHint")}>
          <Select
            options={CURRENCY_OPTIONS}
            value={currency ?? undefined}
            onValueChange={(v) => setCurrency(v as Currency)}
            disabled={!currencyEditable}
          />
        </Field>

        {/* A branch inherits the parish's fiscal year (org-wide, locked), so it is read-only there — like the
            currency above. The parish itself chooses it. */}
        <Field label={t("yearStartLabel")} hint={fiscalYearInherited ? t("yearStartInheritedHint") : t("yearStartHint")}>
          <Select
            options={MONTH_OPTIONS}
            value={startMonth ?? undefined}
            onValueChange={setStartMonth}
            disabled={locked || fiscalYearInherited || parishBlocked}
          />
        </Field>

        {status ? (
          <Field label={t("timezoneLabel")} hint={t("timezoneHint")}>
            <Select
              options={TIMEZONE_OPTIONS}
              value={timezone ?? undefined}
              onValueChange={(value) => setTimezone(value as AccountingTimezone)}
              disabled={locked || parishBlocked}
            />
          </Field>
        ) : null}

        <div className='space-y-1.5'>
          <Label>{t("startDateLabel")}</Label>
          <div className='grid grid-cols-2 gap-2'>
            <Select
              options={MONTH_OPTIONS}
              value={conversionMonth ?? undefined}
              onValueChange={(v) => setConversionDate(toConversionDay(conversionYear ?? String(CURRENT_YEAR), v))}
              disabled={locked || parishBlocked}
            />
            <Select
              options={YEAR_OPTIONS}
              value={conversionYear ?? undefined}
              onValueChange={(v) => setConversionDate(toConversionDay(v, conversionMonth ?? "1"))}
              disabled={locked || parishBlocked}
            />
          </div>
          <p className='text-muted-foreground text-xs leading-relaxed'>{t("startDateHint")}</p>
          {openingAsAt ? (
            <p className='text-primary text-xs font-semibold underline underline-offset-4'>
              {t("openingAsAt", { date: openingAsAt })}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  )
})

/* -------------------------------------------------------------------------------------------------- */
/* Step 2 — Financial accounts                                                                          */
/* -------------------------------------------------------------------------------------------------- */

const ACCOUNT_ICONS: Record<FinanceAccountType, LucideIcon> = {
  [FinanceAccountType.Bank]: LandmarkIcon,
  [FinanceAccountType.Cash]: BanknoteIcon,
  [FinanceAccountType.MobileMoney]: SmartphoneIcon,
}

function resolveSetupAccountOpeningDisplay(account: FinanceAccountRecord, fallback: string) {
  const openingBalance = account.openingBalance ?? account.balance
  return openingBalance != null && !isZeroMoney(openingBalance, account.currency)
    ? formatMoney(openingBalance, account.currency)
    : fallback
}

export function OnboardingAccountsStep({ context, workspaceId, branchId, canEdit }: StepProps) {
  const t = useTranslations("finance.onboarding.accounts")
  const toastError = useApiErrorToast("finance")
  const [dialogOpen, setDialogOpen] = React.useState(false)
  /* The account being corrected. The dialog already knows how to edit one — this step just never asked it to. */
  const [editing, setEditing] = React.useState<FinanceAccountRecord | null>(null)
  const deactivateAccount = useFinanceDeactivateAccountMutation()

  const accountsQuery = useFinanceAccountsQuery({
    workspaceId,
    branchId,
    scope: scopeOf(context),
    includeBalances: true,
  })
  const accounts = (accountsQuery.data?.items ?? []).filter((a) => a.status === "ACTIVE")

  const items: GuidedItem[] = accounts.map((account) => ({
    id: account.id,
    Icon: ACCOUNT_ICONS[account.type] ?? LandmarkIcon,
    title: account.name,
    subtitle: t(`types.${account.type}`),
    meta: resolveSetupAccountOpeningDisplay(account, t("noOpening")),
    /*
     * Editing reaches the OPENING BALANCE, which is the whole point: an account entered without one leaves
     * the allocation step with a total it cannot balance, and the wizard offered no way back to the cause.
     * The dialog itself refuses to send an opening balance once the account is locked by a posting, so this
     * stays honest after the books are open.
     */
    onEdit: () => setEditing(account),
    onRemove: () => {
      deactivateAccount.mutate(account.id, {
        // Removal is a WRITE and can be refused — an account that already holds postings, say. Silence here
        // would recreate the bug this whole change is about.
        onError: (error) => toastError(error),
      })
    },
    removeConfirm: {
      title: t("remove.title"),
      description: t("remove.description", { name: account.name }),
      confirmLabel: t("remove.confirm"),
    },
  }))

  return (
    <>
      <GuidedList
        items={items}
        isLoading={accountsQuery.isLoading}
        addLabel={t("add")}
        onAdd={() => setDialogOpen(true)}
        canEdit={canEdit}
        emptyIcon={LandmarkIcon}
        emptyText={t("empty")}
        editLabel={t("editAction")}
        removeLabel={t("remove.action")}
      />
      <FinanceAccountFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        mode='create'
        context={context}
        workspaceId={workspaceId}
        branchId={branchId}
        scope={scopeOf(context)}
      />
      <FinanceAccountFormDialog
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
        mode='edit'
        record={editing}
        context={context}
        workspaceId={workspaceId}
        branchId={branchId}
        scope={scopeOf(context)}
        onSaved={() => setEditing(null)}
      />
    </>
  )
}

/* -------------------------------------------------------------------------------------------------- */
/* Step 3 — Funds (optional)                                                                            */
/* -------------------------------------------------------------------------------------------------- */

export function OnboardingFundsStep({ context, workspaceId, branchId, canEdit }: StepProps) {
  const t = useTranslations("finance.onboarding.funds")
  const toastError = useApiErrorToast("finance")
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const [editing, setEditing] = React.useState<FinanceFundRecord | null>(null)
  const setFundStatus = useFinanceSetFundStatusMutation()
  const baseCurrency = useBranchCurrency()

  const fundsQuery = useFinanceFundsQuery({
    workspaceId,
    branchId,
    scope: scopeOf(context),
    kind: FinanceFundListKind.Custom,
  })
  const funds = (fundsQuery.data?.items ?? []).filter((f) => f.status === FinanceFundStatus.Active)

  const items: GuidedItem[] = funds.map((fund) => {
    return {
      id: fund.id,
      Icon: PiggyBankIcon,
      title: fund.name,
      subtitle: fund.isRestricted ? t("restricted") : (fund.description ?? null),
      onEdit: () => setEditing(fund),
      onRemove: () => {
        setFundStatus.mutate(
          { id: fund.id, status: FinanceFundStatus.Closed },
          { onError: (error) => toastError(error) },
        )
      },
      removeConfirm: {
        title: t("remove.title"),
        description: t("remove.description", { name: fund.name }),
        confirmLabel: t("remove.confirm"),
      },
    }
  })

  return (
    <>
      <GuidedList
        items={items}
        isLoading={fundsQuery.isLoading}
        addLabel={t("add")}
        onAdd={() => setDialogOpen(true)}
        canEdit={canEdit}
        emptyIcon={PiggyBankIcon}
        emptyText={t("empty")}
        collapsedSummary={t("existing")}
        collapsedCountLabel={t("existingCount", { count: items.length })}
        defaultCollapsed
        editLabel={t("editAction")}
        removeLabel={t("remove.action")}
      />
      <FundCreateDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        context={context}
        workspaceId={workspaceId}
        branchId={branchId}
      />
      {/*
        The FULL fund dialog for edits, not the minimal onboarding create above. Correcting a fund means
        reaching what was actually set on it — a restriction, a target, the dates — and a stripped-down editor
        would quietly drop whatever it did not render.
      */}
      <FundFormDialog
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
        mode='edit'
        record={editing}
        workspaceId={workspaceId}
        branchId={branchId}
        scope={scopeOf(context)}
        baseCurrency={baseCurrency}
        onSaved={() => setEditing(null)}
      />
    </>
  )
}

/**
 * A deliberately minimal fund create for onboarding — name + description only. Advanced restriction and
 * opening-balance allocation live in the full Funds settings page; this just lets a church name a fund quickly.
 */
function FundCreateDialog({
  open,
  onOpenChange,
  context,
  workspaceId,
  branchId,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  context: "branch" | "parish"
  workspaceId: string
  branchId: string
}) {
  const t = useTranslations("finance.onboarding.funds")
  const toastError = useApiErrorToast("finance")
  const createFund = useFinanceCreateSetupFundMutation()
  const isMobile = useIsMobile()
  const [name, setName] = React.useState("")
  const [description, setDescription] = React.useState("")

  const reset = () => {
    setName("")
    setDescription("")
  }

  const submit = async () => {
    const trimmed = name.trim()
    if (!trimmed) return
    try {
      await createFund.mutateAsync({
        workspaceId,
        branchId,
        scope: scopeOf(context),
        name: trimmed,
        description: description.trim() || undefined,
      })
      toast.success(t("created"))
      reset()
      onOpenChange(false)
    } catch (error) {
      toastError(error)
    }
  }

  if (isMobile) {
    return (
      <MobileFormSheet
        open={open}
        onOpenChange={(next) => {
          if (!next) reset()
          onOpenChange(next)
        }}
        title={t("createTitle")}
        note={t("createBody")}
        cancelLabel={t("cancel")}
        saveLabel={t("createAction")}
        onSave={() => void submit()}
        saving={createFund.isPending}
        saveDisabled={!name.trim()}
      >
        <MobileFormSection>
          <MobileFormInputRow label={t("nameLabel")} value={name} onChange={(e) => setName(e.target.value)} placeholder={t("namePlaceholder")} />
        </MobileFormSection>
        <MobileFormSection title={t("descriptionLabel")}>
          <MobileFormTextArea value={description} onChange={(e) => setDescription(e.target.value)} aria-label={t("descriptionLabel")} />
        </MobileFormSection>
      </MobileFormSheet>
    )
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset()
        onOpenChange(next)
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("createTitle")}</DialogTitle>
          <DialogDescription>{t("createBody")}</DialogDescription>
        </DialogHeader>
        <div className='space-y-4'>
          <div className='space-y-1.5'>
            <Label htmlFor='fund-name'>{t("nameLabel")}</Label>
            <Input
              id='fund-name'
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("namePlaceholder")}
            />
          </div>
          <div className='space-y-1.5'>
            <Label htmlFor='fund-description'>{t("descriptionLabel")}</Label>
            <Textarea
              id='fund-description'
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant='ghost' onClick={() => onOpenChange(false)}>
            {t("cancel")}
          </Button>
          <Button onClick={submit} disabled={!name.trim() || createFund.isPending}>
            {t("createAction")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/* -------------------------------------------------------------------------------------------------- */
/* Step 4 — Income & expense accounts (optional)                                                        */
/* -------------------------------------------------------------------------------------------------- */

function isActiveChartAccount(account: ChartAccount): boolean {
  return account.status === "ACTIVE"
}

export function OnboardingCategoriesStep({
  context,
  workspaceId,
  branchId,
  canCreateChartAccounts = false,
  chartOwningBranchId = null,
}: StepProps) {
  const t = useTranslations("finance.onboarding.categories")
  const [dialog, setDialog] = React.useState<ChartAccountKind | null>(null)

  const scope = scopeOf(context)
  const incomeQuery = useAccountingChartQuery({
    workspaceId,
    branchId,
    scope,
    type: AccountingAccountType.Income,
  })
  const expenseQuery = useAccountingChartQuery({
    workspaceId,
    branchId,
    scope,
    type: AccountingAccountType.Expense,
  })

  /* The API sends seeded account names already localized (ADR-0030). */
  const accountName = React.useCallback(
    (account: ChartAccount) => account.name,
    [],
  )

  const incomeAccounts: GuidedItem[] = (incomeQuery.data?.items ?? [])
    .filter(isActiveChartAccount)
    .map((account) => ({
      id: account.id,
      Icon: HandCoinsIcon,
      title: accountName(account),
      subtitle: t("incomeSubtitle"),
      meta: account.code,
    }))
  const expenseAccounts: GuidedItem[] = (expenseQuery.data?.items ?? [])
    .filter(isActiveChartAccount)
    .map((account) => ({
      id: account.id,
      Icon: ReceiptTextIcon,
      title: accountName(account),
      subtitle: t("expenseSubtitle"),
      meta: account.code,
    }))

  const isMobile = useIsMobile()
  const [mobileTab, setMobileTab] = React.useState<ChartAccountKind>(ChartAccountKind.Income)
  const chartDialog = (
    <FinanceChartAccountFormDialog
      open={dialog !== null}
      onOpenChange={(open) => setDialog((current) => (open ? current : null))}
      kind={dialog ?? ChartAccountKind.Income}
      workspaceId={workspaceId}
      owningBranchId={chartOwningBranchId}
    />
  )

  // Phone: the income / expense switch as the app's segmented control, over the selected list.
  if (isMobile) {
    const income = mobileTab === ChartAccountKind.Income
    return (
      <div className='flex flex-col gap-3'>
        <MobileSegmentedControl
          value={mobileTab}
          onChange={setMobileTab}
          items={[
            { value: ChartAccountKind.Income, label: t("incomeTitle") },
            { value: ChartAccountKind.Expense, label: t("expenseTitle") },
          ]}
        />
        <GuidedList
          key={mobileTab}
          items={income ? incomeAccounts : expenseAccounts}
          isLoading={income ? incomeQuery.isLoading : expenseQuery.isLoading}
          addLabel={income ? t("addIncome") : t("addExpense")}
          onAdd={() => setDialog(mobileTab)}
          canEdit={canCreateChartAccounts}
          emptyIcon={income ? HandCoinsIcon : ReceiptTextIcon}
          emptyText={income ? t("incomeEmpty") : t("expenseEmpty")}
          collapsible={false}
        />
        {chartDialog}
      </div>
    )
  }

  return (
    <>
      <Tabs defaultValue='income' className='w-full'>
        <TabsList className='w-full justify-start overflow-x-auto overflow-y-hidden whitespace-nowrap [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'>
          <TabsTrigger value='income'>{t("incomeTitle")}</TabsTrigger>
          <TabsTrigger value='expense'>{t("expenseTitle")}</TabsTrigger>
        </TabsList>

        <TabsContent value='income' className='mt-5'>
          <GuidedList
            items={incomeAccounts}
            isLoading={incomeQuery.isLoading}
            addLabel={t("addIncome")}
            onAdd={() => setDialog(ChartAccountKind.Income)}
            canEdit={canCreateChartAccounts}
            emptyIcon={HandCoinsIcon}
            emptyText={t("incomeEmpty")}
            /*
              Ibada Cloud seeds a full chart, but this setup step is explicitly about reviewing those accounts.
              Keep the summary chrome, but render the rows by default and do not let the header collapse them.
            */
            collapsedSummary={t("existingIncome")}
            collapsedCountLabel={t("existingCount", { count: incomeAccounts.length })}
            defaultCollapsed
            collapsible={false}
          />
        </TabsContent>

        <TabsContent value='expense' className='mt-5'>
          <GuidedList
            items={expenseAccounts}
            isLoading={expenseQuery.isLoading}
            addLabel={t("addExpense")}
            onAdd={() => setDialog(ChartAccountKind.Expense)}
            canEdit={canCreateChartAccounts}
            emptyIcon={ReceiptTextIcon}
            emptyText={t("expenseEmpty")}
            collapsedSummary={t("existingExpense")}
            collapsedCountLabel={t("existingCount", { count: expenseAccounts.length })}
            defaultCollapsed
            collapsible={false}
          />
        </TabsContent>
      </Tabs>
      <FinanceChartAccountFormDialog
        open={dialog !== null}
        onOpenChange={(open) => setDialog((current) => (open ? current : null))}
        kind={dialog ?? ChartAccountKind.Income}
        workspaceId={workspaceId}
        owningBranchId={chartOwningBranchId}
      />
    </>
  )
}
